/**
 * Package availability — pure helpers (no React, no Redux).
 *
 * The BE stores the dates but does NOT enforce them on estimates/orders; the FE does. All values are calendar
 * dates (`YYYY-MM-DD`, no time zone) and are compared as plain strings, which orders them correctly. `null` /
 * absent means "no limit" on that side, and an unknown/empty date is never treated as a violation.
 *
 *  - `packageAvailabilityFrom`  → the first EVENT date it can be booked for;
 *  - `packageAvailabilityTo`    → the last EVENT date it can be booked for (and the package is Expired after it);
 *  - `orderStartingDate`        → orders for it can be taken from this date (before it the picker shows it greyed out).
 */
import { formatDate } from './formatDate';
import type { CateringPackage } from '../data/types';

export type PackageStatus = 'active' | 'expired';

export type PackageWindow = Pick<CateringPackage, 'orderStartingDate' | 'packageAvailabilityFrom' | 'packageAvailabilityTo'>;

const YMD = /^\d{4}-\d{2}-\d{2}$/;

export const isYmd = (value: unknown): value is string => typeof value === 'string' && YMD.test(value);

/** Normalises a stored date: a valid `YYYY-MM-DD` or `null`. */
const date = (value: string | null | undefined): string | null => (isYmd(value) ? value : null);

/** The calendar day of an event date: `YYYY-MM-DD`, or the date part of an ISO timestamp; `null` for empty / unreadable. */
const eventDay = (value: string | null | undefined): string | null => {
  const m = typeof value === 'string' ? /^(\d{4}-\d{2}-\d{2})(?:$|T)/.exec(value.trim()) : null;
  return m ? m[1] : null;
};

export const hasAvailabilityWindow = (pkg: PackageWindow): boolean =>
  !!(date(pkg.orderStartingDate) || date(pkg.packageAvailabilityFrom) || date(pkg.packageAvailabilityTo));

// ── Status & listing ────────────────────────────────────────────────────────────

/** Expired once the availability end date has passed (the end date itself is still active). Everything else is Active. */
export function packageStatus(pkg: PackageWindow, today: string): PackageStatus {
  const to = date(pkg.packageAvailabilityTo);
  return to && isYmd(today) && to < today ? 'expired' : 'active';
}

export interface PackagePickerState {
  /** Offered in the line-item picker at all. */
  listed: boolean;
  /** Orders for it open later than today: listed greyed out and not selectable, "Order starts from Dec 10". */
  opensOn: string | null;
}

/**
 * How the order / estimate line-item picker offers this package.
 *  - LISTED only when the EVENT date falls inside its availability window `[from, to]` — whether orders are open today
 *    does not decide it. No event date yet: listed unless it has already ended. A package past its last day is never offered.
 *  - Listed but orders not open yet (today is before `orderStartingDate`): `opensOn` is that date — shown greyed out.
 */
export function packagePickerState(
  pkg: PackageWindow,
  today: string | null | undefined,
  eventDate: string | null | undefined,
): PackagePickerState {
  const to = date(pkg.packageAvailabilityTo);
  const ended = isYmd(today) && !!to && today > to;
  if (ended || !isPackageBookableOn(pkg, eventDate)) return { listed: false, opensOn: null };
  const start = date(pkg.orderStartingDate);
  return { listed: true, opensOn: start && isYmd(today) && today < start ? start : null };
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "Dec 10" for a `YYYY-MM-DD` day — with the year ("Dec 10, 2027") when it is not in today's year. Anything else comes back unchanged. */
export function shortDayLabel(ymd: string, today?: string | null): string {
  if (!isYmd(ymd)) return ymd;
  const [year, month, day] = ymd.split('-').map(Number);
  const text = `${MONTHS[month - 1]} ${day}`;
  return isYmd(today) && Number(today.slice(0, 4)) !== year ? `${text}, ${year}` : text;
}

// ── Event-date check ────────────────────────────────────────────────────────────

/** Is the event date inside `[packageAvailabilityFrom, packageAvailabilityTo]` (inclusive, null side skipped)? An empty event date passes. */
export function isPackageBookableOn(pkg: PackageWindow, eventDate: string | null | undefined): boolean {
  const day = eventDay(eventDate);
  if (!day) return true;
  const from = date(pkg.packageAvailabilityFrom);
  const to = date(pkg.packageAvailabilityTo);
  if (from && day < from) return false;
  if (to && day > to) return false;
  return true;
}

/** The booking window as prose for a message: "from A to B", "from A onwards", "up to B". Empty when unlimited. */
export function bookingWindowText(pkg: PackageWindow, country?: string | null): string {
  const from = date(pkg.packageAvailabilityFrom);
  const to = date(pkg.packageAvailabilityTo);
  if (from && to) return `from ${formatDate(from, country)} to ${formatDate(to, country)}`;
  if (from) return `from ${formatDate(from, country)} onwards`;
  if (to) return `up to ${formatDate(to, country)}`;
  return '';
}

/** Names the package and its window when the event date falls outside it; `undefined` when it is fine (or nothing to check). */
export function packageUnavailableMessage(
  pkg: Pick<CateringPackage, 'name'> & PackageWindow,
  eventDate: string | null | undefined,
  country?: string | null,
): string | undefined {
  if (isPackageBookableOn(pkg, eventDate)) return undefined;
  return `${pkg.name} is only available for events ${bookingWindowText(pkg, country)}.`;
}

/**
 * The message on a package LINE of the estimate / order form when the event date falls outside its window — the line
 * already shows which package it is: "This package is available only from A to B." `undefined` when it is fine.
 */
export function packageLineWindowMessage(pkg: PackageWindow, eventDate: string | null | undefined, country?: string | null): string | undefined {
  if (isPackageBookableOn(pkg, eventDate)) return undefined;
  return `This package is available only ${bookingWindowText(pkg, country)}.`;
}

/** A line of an estimate/order form (or a saved estimate line) — only what the check needs. */
export interface PackageLineRef {
  id?: string;
  type?: string;
  packageId?: string;
}

/**
 * Row id → message, for every package line whose live package does not cover the event date. A package that no
 * longer exists (deleted) has no known window, so it is skipped. Pure: callers pass the packages and the date.
 */
export function lineAvailabilityIssues(
  lines: readonly PackageLineRef[],
  packageById: ReadonlyMap<string, CateringPackage>,
  eventDate: string | null | undefined,
  country?: string | null,
): Record<string, string> {
  const issues: Record<string, string> = {};
  if (!eventDay(eventDate)) return issues;
  lines.forEach((line, index) => {
    if (line.type !== 'package' || !line.packageId) return;
    const pkg = packageById.get(line.packageId);
    if (!pkg) return;
    const message = packageUnavailableMessage(pkg, eventDate, country);
    if (message) issues[line.id ?? `#${index}`] = message;
  });
  return issues;
}

/** The distinct messages of `lineAvailabilityIssues` (one per package even if it sits on several lines). */
export function availabilityMessages(issues: Record<string, string>): string[] {
  return [...new Set(Object.values(issues))];
}

// ── Labels for the list ─────────────────────────────────────────────────────────

/** "15/10/2026 - 31/10/2026", "From 15/10/2026", "Until 31/10/2026" or "Always". */
export function availabilityLabel(pkg: PackageWindow, country?: string | null): string {
  const from = date(pkg.packageAvailabilityFrom);
  const to = date(pkg.packageAvailabilityTo);
  if (from && to) return `${formatDate(from, country)} - ${formatDate(to, country)}`;
  if (from) return `From ${formatDate(from, country)}`;
  if (to) return `Until ${formatDate(to, country)}`;
  return 'Always';
}

/** The order-starting date, or "Always" when orders can be placed at any time. */
export function orderStartLabel(pkg: PackageWindow, country?: string | null): string {
  const start = date(pkg.orderStartingDate);
  return start ? formatDate(start, country) : 'Always';
}

// ── Form validation ─────────────────────────────────────────────────────────────

export interface AvailabilityDatesInput {
  from: string;
  to: string;
  orderStart: string;
}

export interface AvailabilityErrors {
  from?: string;
  to?: string;
  orderStart?: string;
}

/**
 * `from <= to` and `orderStartingDate <= to` (the BE rules), plus — form only — `orderStartingDate <= from`: orders
 * must open on or before the first event date they can be booked for. Empty values are "no limit" and never an error.
 */
export function validateAvailabilityDates({ from, to, orderStart }: AvailabilityDatesInput): AvailabilityErrors {
  const errors: AvailabilityErrors = {};
  if (isYmd(from) && isYmd(to) && from > to) errors.to = 'End date must be on or after the start date';
  if (isYmd(orderStart) && isYmd(from) && orderStart > from) {
    errors.orderStart = 'Order starting date must be on or before the start date';
  } else if (isYmd(orderStart) && isYmd(to) && orderStart > to) {
    errors.orderStart = 'Order starting date must be on or before the availability end date';
  }
  return errors;
}
